import { jobEngineApi } from "@/api";

// ---------- Shared types ----------
export type JobState = "Enqueued" | "Started" | "Succeeded" | "Failed" | "DeadLettered";

export interface Job {
  jobId: string;
  jobType: string;
  state: JobState;
  correlationId: string;
  parentJobId?: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  finalState: JobState | null;
  attemptCount: number;
  lastError: string | null;
  payload?: unknown;
}

export interface JobEvent {
  eventId: string;
  jobId: string;
  state: JobState;
  occurredAt: string;
  detail?: string | null;
}

export interface RecurringJob {
  recurringId: string;
  name: string;
  jobType: string;
  cron: string;
  enabled: boolean;
  payload: unknown;
  nextFireUtc: string | null;
  lastFireUtc: string | null;
}

export interface DeadLetter {
  deadLetterId: string;
  jobId: string;
  jobType: string;
  correlationId: string;
  deadLetteredAt: string;
  errorSummary: string;
  errorFull: string;
  payload: unknown;
}

export interface DashboardTiles {
  inFlightCount: number;
  succeededPerMinute: number;
  failedPerMinute: number;
  deadLetterDepthTotal: number;
  deadLetterDepthByHandler: Array<{ jobType: string; depth: number }>;
}

export interface DashboardChartSeries {
  window: "1h" | "24h";
  throughput: Array<{ timestamp: string; succeeded: number; failed: number }>;
  errorRate: Array<{ timestamp: string; rate: number }>;
  pickupLatencyP95ByHandler: Array<{ jobType: string; p95Ms: number }>;
}

export interface JobTypesMeta {
  jobTypes: Array<{ jobType: string; description?: string }>;
}

export interface PagedResult<T> { items: T[]; totalCount: number; }

// ---------- Jobs queries ----------
export interface JobsQuery {
  state?: JobState | null;
  jobType?: string | null;
  correlationId?: string | null;
  fromCreatedAtUtc?: string | null;
  toCreatedAtUtc?: string | null;
  pageNumber: number;
  pageSize: number;
}

export const getJobs = async (q: JobsQuery, signal?: AbortSignal): Promise<PagedResult<Job>> => {
  const r = await jobEngineApi.get<PagedResult<Job>>("/v1/jobs", { params: q, signal });
  return r.data;
};

export const getJob = async (id: string, signal?: AbortSignal): Promise<Job> => {
  const r = await jobEngineApi.get<Job>(`/v1/jobs/${encodeURIComponent(id)}`, { signal });
  return r.data;
};

export const getJobEvents = async (id: string, signal?: AbortSignal): Promise<JobEvent[]> => {
  const r = await jobEngineApi.get<JobEvent[]>(`/v1/jobs/${encodeURIComponent(id)}/events`, { signal });
  return r.data;
};

/**
 * Returns jobs whose ParentJobId equals the given parent jobId, oldest-first.
 * Backed by GET /v1/jobs/{jobId}/children.
 */
export const getJobChildren = async (id: string, signal?: AbortSignal): Promise<Job[]> => {
  const r = await jobEngineApi.get<Job[]>(`/v1/jobs/${encodeURIComponent(id)}/children`, { signal });
  return r.data;
};

// ---------- Lineage ----------
export interface JobLineageNode {
  jobId: string;
  jobType: string;
  state: JobState;
  finalState: JobState | null;
  parentJobId: string | null;
  correlationId: string | null;
  /** Origin of the publish — "api", "scheduled", "workbench", "producer" (generic), or null for older rows. */
  origin: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  attemptCount: number;
  lastError: string | null;
  depth: number;
}

export interface JobLineage {
  rootJobId: string;
  nodes: JobLineageNode[];
  truncated: boolean;
}

/**
 * Returns the descendant subtree rooted at jobId — the job itself plus every
 * transitive child via dbo.Jobs.ParentJobId. Bounded by the server at depth 10
 * and 1000 nodes; check `truncated` on the response.
 *
 * Backed by GET /v1/jobs/{jobId}/lineage.
 */
export const getJobLineage = async (id: string, signal?: AbortSignal): Promise<JobLineage> => {
  const r = await jobEngineApi.get<JobLineage>(`/v1/jobs/${encodeURIComponent(id)}/lineage`, { signal });
  return r.data;
};

export const retryJob = async (id: string): Promise<void> => {
  await jobEngineApi.post(`/v1/jobs/${encodeURIComponent(id)}/retry`);
};

// ---------- Recurring ----------
export interface RecurringUpsert {
  name: string;
  jobType: string;
  cron: string;
  enabled: boolean;
  payload: unknown;
}

export const getRecurring = async (signal?: AbortSignal): Promise<RecurringJob[]> => {
  const r = await jobEngineApi.get<RecurringJob[]>("/v1/recurring", { signal });
  return r.data;
};

export const getRecurringSchedule = async (id: string, signal?: AbortSignal): Promise<RecurringJob> => {
  const r = await jobEngineApi.get<RecurringJob>(`/v1/recurring/${encodeURIComponent(id)}`, { signal });
  return r.data;
};

export const createRecurring = async (input: RecurringUpsert): Promise<RecurringJob> => {
  const r = await jobEngineApi.post<RecurringJob>("/v1/recurring", input);
  return r.data;
};

export const updateRecurring = async (id: string, input: RecurringUpsert): Promise<RecurringJob> => {
  const r = await jobEngineApi.put<RecurringJob>(`/v1/recurring/${encodeURIComponent(id)}`, input);
  return r.data;
};

export const deleteRecurring = async (id: string): Promise<void> => {
  await jobEngineApi.delete(`/v1/recurring/${encodeURIComponent(id)}`);
};

export const toggleRecurring = async (id: string, enabled: boolean): Promise<void> => {
  await jobEngineApi.post(`/v1/recurring/${encodeURIComponent(id)}/toggle`, { enabled });
};

export const triggerRecurringNow = async (id: string): Promise<{ jobId: string }> => {
  const r = await jobEngineApi.post<{ jobId: string }>(`/v1/recurring/${encodeURIComponent(id)}/trigger-now`);
  return r.data;
};

// ---------- Dead letters ----------
export interface DeadLettersQuery {
  jobType?: string | null;
  pageNumber: number;
  pageSize: number;
}

export const getDeadLetters = async (q: DeadLettersQuery, signal?: AbortSignal): Promise<PagedResult<DeadLetter>> => {
  const r = await jobEngineApi.get<PagedResult<DeadLetter>>("/v1/dead-letters", { params: q, signal });
  return r.data;
};

export const getDeadLetter = async (id: string, signal?: AbortSignal): Promise<DeadLetter> => {
  const r = await jobEngineApi.get<DeadLetter>(`/v1/dead-letters/${encodeURIComponent(id)}`, { signal });
  return r.data;
};

export const requeueDeadLetter = async (id: string): Promise<void> => {
  await jobEngineApi.post(`/v1/dead-letters/${encodeURIComponent(id)}/requeue`);
};

export const requeueDeadLettersBulk = async (ids: string[]): Promise<void> => {
  await jobEngineApi.post("/v1/dead-letters/requeue-bulk", { ids });
};

// ---------- Workbench ----------
export interface WorkbenchPublishRequest {
  jobType: string;
  payload: unknown;
  messageId: string;
  correlationId?: string | null;
  idempotencyKey?: string | null;
}

export const publishWorkbench = async (req: WorkbenchPublishRequest): Promise<{ jobId: string }> => {
  const r = await jobEngineApi.post<{ jobId: string }>("/v1/workbench/publish", req);
  return r.data;
};

// ---------- Dashboard ----------
export const getDashboardTiles = async (signal?: AbortSignal): Promise<DashboardTiles> => {
  const r = await jobEngineApi.get<DashboardTiles>("/v1/dashboard/tiles", { signal });
  return r.data;
};

export const getDashboardCharts = async (window: "1h" | "24h", signal?: AbortSignal): Promise<DashboardChartSeries> => {
  const r = await jobEngineApi.get<DashboardChartSeries>("/v1/dashboard/charts", { params: { window }, signal });
  return r.data;
};

// ---------- Meta ----------
export const getJobTypesMeta = async (signal?: AbortSignal): Promise<JobTypesMeta> => {
  const r = await jobEngineApi.get<JobTypesMeta>("/v1/meta/job-types", { signal });
  return r.data;
};

// ---------- Realtime event payloads ----------
export interface JobStartedEvent      { jobId: string; startedAt: string; }
export interface JobFinishedEvent     { jobId: string; finishedAt: string; finalState: JobState; }
export interface JobFailedEvent       { jobId: string; error: string; }
export interface JobDeadLetteredEvent { jobId: string; jobType: string; reason: string; }

export type JobEngineEventName = "JobStarted" | "JobFinished" | "JobFailed" | "JobDeadLettered";
